import {
  Component,
  ElementRef,
  ViewChild,
  effect,
  input,
  WritableSignal,
  output,
  signal,
  Signal,
  ChangeDetectionStrategy,
} from '@angular/core'
import { CoreService } from '../../../services'
import { TranslateService } from '@ngx-translate/core'
import { ExportsComponent } from '../../../pages/exports/exports.component'
import { Carrier, Depot, Payment } from 'depoto-core/src/entities'

@Component({
  selector: 'app-filter-form',
  templateUrl: './filter-form.component.html',
  styleUrls: ['./filter-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FilterFormComponent {
  @ViewChild('exportsModal') exportsModal: ExportsComponent
  filters = input.required<any>() // todo type
  defaultFilters = input<any>()
  allFilters = input<any>()
  /**
   * Keys for filters rendered in the collapsed advanced section.
   * Used to auto-expand the section on page load when any hidden filter is active.
   */
  advancedFilterKeys = input<readonly string[]>([])
  filtersForExport = input.required<any>() // todo type
  loading = input<boolean>(false)
  entity = input.required<string>()
  filterVisible = input<WritableSignal<boolean>>(signal<boolean>(false))

  isFilterInvisible = input<boolean>(false)
  areExportsInFilter = input<boolean>(false)
  areAllProductsChecked = input<Signal<boolean>>(signal<boolean>(false))
  areAllOrdersChecked = input<any>(false)
  checkedProducts = input<Signal<any>>()
  checkedOrders = input<any>() // todo

  checkedMap = input<WritableSignal<Record<string, boolean>>>()

  tags = input<any>([])
  isInProducts = input<boolean>(false)
  isInOrders = input<boolean>(false)
  isPurchaseOrderShown = input<boolean>(false)
  isExportButtonShown = input<boolean>(false)
  isShowingTags = input<boolean>(false)

  totalItems = input<number>(0)
  items = input<any>()

  payments = input<Payment[]>([])
  carriers = input<Carrier[]>([])
  depots = input<Depot[]>([])

  onSubmit = output<void>()
  onExportResult = output<any>() // todo
  onClearClick = output<void>()
  setCheckedProductsToFalse = output<void>()
  setCheckedOrdersToFalse = output<void>()
  onPurchaseOrderClick = output<void>()
  onCreateModalHidden = output<any>()
  onDownloadInvoices = output<any>()
  private hasCheckedActiveAdvancedFilters = false

  constructor(
    private core: CoreService,
    private translateService: TranslateService,
    private elementRef: ElementRef<HTMLElement>
  ) {
    effect(() => {
      if (this.hasCheckedActiveAdvancedFilters) return

      const allFilters = this.allFilters()
      const defaultFilters = this.defaultFilters()
      const advancedFilterKeys = this.advancedFilterKeys()

      if (!allFilters || !defaultFilters || !advancedFilterKeys?.length) return

      if (advancedFilterKeys.some(key => this.isAdvancedFilterActive(allFilters[key], defaultFilters[key]))) {
        this.hasCheckedActiveAdvancedFilters = true
        this.filterVisible().set(true)
      }
    })
  }

  public triggerOnSubmit(): void {
    // Only VISIBLE invalid inputs may veto the submit. Collapsed advanced
    // sections keep required date inputs permanently :invalid while hidden
    // (e.g. /products advancedPrice/percentageDiscount date ranges) — the
    // original querySelector('input:invalid') found those and silently killed
    // every Filter click on such pages (user-facing BUG 1).
    const invalidInput = Array.from(
      this.elementRef.nativeElement.querySelectorAll<HTMLInputElement>('input:invalid')
    ).find(i => i.offsetParent !== null)
    if (invalidInput) {
      invalidInput.reportValidity()
      invalidInput.focus()
      return
    }

    // BUG 1 fix (2026-07-31): do NOT abortAllRequests() here. Aborting kills the
    // in-flight TanStack fetch externally, and the refetch() the consumers call
    // right after is then swallowed by the query in the same tick — the click
    // fires ZERO network requests (/orders, /users, /sold-items, product moves).
    // The Enter path never aborted and always worked; click now matches it.
    // this.onSubmit.emit(true)
    this.onSubmit.emit()
  }

  public triggerOnClear() {
    // Same mechanism as triggerOnSubmit — the abort swallowed the clear refetch.
    this.filterVisible().set(false)
    this.onClearClick.emit()
  }

  public triggerExportResult(result: any) {
    this.onExportResult.emit(result)
  }

  public triggerOnVisibilityChange() {
    this.filterVisible().set(structuredClone(!this.filterVisible()()))
  }

  refreshProduct() {
    this.onSubmit.emit()
  }

  refreshOrder() {
    this.onSubmit.emit()
  }

  setCheckedProductsToFalseMethod() {
    this.setCheckedProductsToFalse.emit()
  }

  // todo refactor better
  setCheckedOrdersToFalseMethod() {
    this.setCheckedOrdersToFalse.emit()
  }

  public checkIfAtLeastOneItemIsSelected(): void {
    if (!this.areAllProductsChecked()() && this.checkedProducts()()?.length < 1) {
      alert(this.translateService.instant('filter-form.activate.at.least.one.product'))
      return
    } else {
      this.onPurchaseOrderClick.emit()
    }
  }

  /**
   * Collect selected order IDs and emit them so the parent can handle
   * downloading invoices for multiple orders.
   */
  public async downloadInvoicesForSelectedOrders(type: string) {
    const fromCheckedOrders = Array.isArray(this.checkedOrders?.()) ? (this.checkedOrders() as any[]) : []

    if (fromCheckedOrders.length) {
      const ids: number[] = fromCheckedOrders
        .map((o: any) => (typeof o?.id !== 'undefined' ? Number(o.id) : undefined))
        .filter((x: any) => !Number.isNaN(x))
      this.onDownloadInvoices.emit({ type: type, ids: ids })
    } else {
      this.onDownloadInvoices.emit({ type: type, ids: [] })
    }
  }

  private isAdvancedFilterActive(value: any, defaultValue: any): boolean {
    return JSON.stringify(this.normalizeFilterValue(value)) !== JSON.stringify(this.normalizeFilterValue(defaultValue))
  }

  private normalizeFilterValue(value: any): any {
    if (typeof value === 'string') {
      const trimmed = value.trim()
      return trimmed && trimmed !== 'all' ? trimmed : null
    }

    if (Array.isArray(value)) {
      if (!value.length) return null
      return value.map(item => this.normalizeFilterValue(item))
    }

    if (value && typeof value === 'object') {
      const normalized = Object.keys(value)
        .sort()
        .reduce<Record<string, any>>((acc, key) => {
          const normalizedValue = this.normalizeFilterValue(value[key])
          if (normalizedValue !== null && normalizedValue !== undefined) {
            acc[key] = normalizedValue
          }
          return acc
        }, {})

      return Object.keys(normalized).length ? normalized : null
    }

    return value === undefined ? null : value
  }
}
